// ─────────────────────────────────────────────────────────────────────────────
// Routing the Trend icon — the PURE decision layer.
//
// One icon per Batch Results row resolves, without the reviewer choosing, WHICH
// chart belongs to that row: a QC control chart (recovery / RPD / blank-vs-RL), a
// paired precision chart, or a client sample's location history against its MCL.
//
// This module is a ROUTER, not a chart engine (guide "The one rule to keep",
// standing rule 8): it decides which endpoint + statistic a row needs and never
// computes a limit. It is kept pure and dependency-free so the routing table and
// the SysSampleCode pairing can be unit-tested without rendering the 3.9k-line
// SdmsPackageDetail grid — see trendRouting.test.ts, which pins every row of the
// routing table and both pair directions.
//
// The classification is READ from the QC Types config (the dbo.QCType chain the
// reader now joins) — the grid has no QCRole/QCRootRole/QCSource columns and
// gains none. See docs "Routing the Trend Icon" (2026-08-31).
// ─────────────────────────────────────────────────────────────────────────────

/** The minimal row shape the router needs — the eight backend fields plus the
 *  identity/site fields already on a Batch Results row. */
export interface TrendRow {
  id: string;
  sys: string | null;
  parameter: string | null;
  siteName?: string | null;
  /** Project.ProjectId (the grid's ProjectID). The Site → Project fallback (v10
   *  §1b): a client sample with no {@link siteName} but a project trends by project
   *  instead. */
  projectName?: string | null;
  parameterOid?: string | null;
  qcTypeName?: string | null;
  qcRole?: string | null;
  qcRootRole?: string | null;
  qcSource?: string | null;
  parentSys?: string | null;
  testMethodOid?: string | null;
  qcTypeOid?: string | null;
  /** Testparameter.InternalStandard — true for an internal standard (e.g. Yttrium
   *  on an ICP run). Lab-flagged per (parameter × method), so no hardcoded list. */
  isInternalStandard?: boolean | null;
  /** SpreadSheetEntry_SampleInfo.uqTestSurrogateID = 1 — true for a surrogate (a
   *  spiked recovery check riding on a sample, not a reported analyte). Surrogates
   *  carry no QC type, so this explicit flag (not the QC chain) routes them. */
  isSurrogate?: boolean | null;
  /** The row's AnalyzedDate (ISO, as the grid received it). It ANCHORS the
   *  trend window: the chart plots the newest N points up to and including this
   *  row, instead of the newest N overall. Without it a batch analysed months ago
   *  charts today's QC and silently omits the very result being reviewed — on
   *  QB26050504_035 the CCV Low2 row reads 197.8% against a 90-110% window while
   *  the chart, plotting 09/11-09/25, reported "In control" (user report
   *  2026-09-28). Optional: a surface that has no date keeps the old behaviour. */
  analyzedDate?: string | null;
}

/** Which numeric column a QC panel charts (mirrors the API's `Field`). The first
 *  three are chemistry (Levey-Jennings); `rng` / `logc` / `growth` are the SM 9020 B
 *  micro columns (duplicate log-range / positive-control log₁₀ count / blank
 *  sterility), and `pa` is the Presence/Absence positive-control attribute — all
 *  served by the SAME control-chart series engine when `scope.mode` is `micro`
 *  (Wilson's ControlChartStatistics — reused, not re-derived). `pa` is never sent
 *  as a hint: the engine DETECTS a qualitative (Present/Absent, no count) test from
 *  the data and charts `pa`/`growth` itself — see the micro branch in resolveOne. */
export type TrendField = 'rec' | 'rpd' | 'res' | 'rng' | 'logc' | 'growth' | 'pa';

/** One chart panel. A route carries one panel, except a paired spike, which
 *  carries two (recovery across both halves, then the D-member's RPD). */
export interface TrendPanel {
  /** `qc` → the control-chart series endpoint; `client` → the site-trend endpoint;
   *  `surrogate` → a surrogate compound's recovery series (dedicated endpoint),
   *  charted against its Low/High acceptance limits with points coloured by
   *  parent-sample category; `isresp` → an internal standard's Response % series
   *  (the v10 Internal Standard Response Trend — dedicated endpoint, Current ÷
   *  batch-average × 100), same chart shape as surrogate. (`is` was the earlier
   *  site-trend based IS recovery, superseded by `isresp`.) */
  kind: 'qc' | 'client' | 'is' | 'surrogate' | 'isresp';
  /** Panel heading, e.g. "Recovery", "RPD", "Blank vs RL", "Site trend". */
  label: string;
  /** QC-panel only: the charted statistic. */
  field?: TrendField;
  /** QC-panel only: the QC type name(s) to scope the series to. A recovery panel
   *  of a pair carries BOTH (MS + MSD); the RPD panel carries the D-member only. */
  qcTypes?: string[];
  /** QC-panel (limit chart) only: override the plotted-value legend label. A paired
   *  MSD/LCSD recovery panel labels its points "MSD Recovery" / "LCSD Recovery"
   *  (v10 §2d); when unset the loader picks a default per field. */
  pointLabel?: string;
  /** Client-panel only: the sampling location to filter the batch trend to (the
   *  Site grouping). Mutually exclusive with {@link project}. */
  site?: string | null;
  /** Client-panel only: the Project.ProjectId to filter the batch trend to (the
   *  Site → Project fallback grouping, v10 §1b). Set instead of {@link site} when
   *  the row has no sampling location but a project; the popup then calls the
   *  project-scoped trend endpoint and labels the header "Project". */
  project?: string | null;
  /** QC-panel only: SM 9020 B micro chart — the popup sends `scope.mode = 'micro'`
   *  so the series endpoint parses the count from `Result` and applies the micro
   *  statistics (range 3.27R̄ / individuals-log / sterility). */
  micro?: boolean;
}

/** Where the classification came from, shown in the popup so the route is never
 *  a black box. `none` means the icon is disabled. */
export type ClassSource = 'root' | 'role' | 'name' | 'is' | 'surrogate' | 'none';

/** The resolved route for one row. */
export interface TrendRoute {
  /** False → render the icon disabled with {@link reason} as its tooltip. */
  routable: boolean;
  reason?: string;
  /** Short route summary for the tooltip + popup header ("Recovery + RPD"). */
  chartLabel: string;
  panels: TrendPanel[];
  /** Resolved root role (Sample / Blank / Standard / Spike / Duplicate / IS). */
  classification: string;
  classSource: ClassSource;
  qcTypeName?: string | null;
  qcRole?: string | null;
  qcSource?: string | null;
  /** "MS1 ↔ MSD1" when this row is half a duplicate pair. */
  pairLabel?: string | null;
  // Scope shared by the panels.
  testMethodOid?: string | null;
  parameter?: string | null;
  /** Testparameter.Parameter Oid — the compound identity a surrogate panel needs
   *  to scope its recovery series precisely (the name can repeat across methods). */
  parameterOid?: string | null;
  /** The row's sampling location, if any — shown as context in the §2b Duplicate RPD
   *  header ("Site: … when available", from the parent sample). */
  siteName?: string | null;
  /** {@link TrendRow.analyzedDate} — the window anchor, sent as the series
   *  scope's `to` so the chart ENDS at the row you opened it from. */
  anchorDate?: string | null;
}

// ── Classification ───────────────────────────────────────────────────────────

const ROOTS = ['Sample', 'Blank', 'Standard', 'Spike', 'Duplicate'] as const;

/** Canonicalise a root-role / role string to one of the five known roots — the
 *  QCRoot_Role and QC_Role columns share this vocabulary. */
function canonRoot(value: string | null | undefined): string | null {
  const v = (value ?? '').trim();
  if (!v) return null;
  const hit = ROOTS.find((r) => r.toLowerCase() === v.toLowerCase());
  return hit ?? null;
}

/** Last-resort classification from the QC type NAME (a lab convention, not a
 *  configured classification — so it is tried only after root role and role). */
function classifyByTypeName(name: string | null | undefined): string | null {
  const n = (name ?? '').trim().toUpperCase();
  if (!n) return null;
  // A QC type literally named "Sample" IS a client sample. On this database the
  // "Sample" QCType carries no QCRootRole/QcRole (verified live 2026-09-02:
  // 'Sample|null|null|null'), so the name is the only signal that routes those
  // 15-per-batch field-sample rows to the site trend instead of disabling them.
  if (n === 'SAMPLE') return 'Sample';
  if (/^(MB|CCB)/.test(n)) return 'Blank';
  if (/^(ICV|CCV)/.test(n)) return 'Standard';
  if (/^DUP/.test(n)) return 'Duplicate';
  if (/^(LCSD|LCS|MSD|MS)/.test(n)) return 'Spike';
  return null;
}

/** The root-role classification of a row from its QC-type chain ALONE (the same
 *  root → role → type-name ladder {@link classify} uses, minus the internal-standard
 *  check, which the caller handles from the explicit flag). Returns one of the five
 *  known roots ('Sample' | 'Blank' | 'Standard' | 'Spike' | 'Duplicate') or null when
 *  the chain carries no classification.
 *
 *  Exported so the SDMS Result Validation grid can bucket rows into Sample vs QC tabs
 *  WITHOUT re-deriving the vocabulary (standing rule 8 — reuse, never reinvent). Kept
 *  in lock-step with `classify` below — both walk qcRootRole → qcRole → qcTypeName. */
export function classifyRootRole(
  row: Pick<TrendRow, 'qcRootRole' | 'qcRole' | 'qcTypeName'>,
): string | null {
  return canonRoot(row.qcRootRole) ?? canonRoot(row.qcRole) ?? classifyByTypeName(row.qcTypeName);
}

interface Classified {
  root: string;
  source: ClassSource;
}

function classify(row: TrendRow, internalStandards: Set<string>): Classified {
  // Internal standards (e.g. Yttrium on an ICP run) ride on every sample but are
  // not reported analytes, so they must never fall through to a site trend.
  // Checked BEFORE the root role. The list is lab-supplied (never hardcoded) —
  // empty until the lab provides it, so no analyte is wrongly captured.
  const analyte = (row.parameter ?? '').trim().toLowerCase();
  if (analyte && internalStandards.has(analyte)) return { root: 'InternalStandard', source: 'is' };

  const byRoot = canonRoot(row.qcRootRole);
  if (byRoot) return { root: byRoot, source: 'root' };

  const byRole = canonRoot(row.qcRole);
  if (byRole) return { root: byRole, source: 'role' };

  const byName = classifyByTypeName(row.qcTypeName);
  if (byName) return { root: byName, source: 'name' };

  return { root: '', source: 'none' };
}

// ── Pairing from SysSampleCode ───────────────────────────────────────────────

interface Suffix {
  token: string;
  seq: string;
}

/** The QC suffix of a code, split into token + sequence. With a parent, the
 *  suffix is the code minus the parent prefix ("…-001MSD1" → "MSD1"); a batch QC
 *  code (LCS1, CCB1) has no parent, so the trailing `<letters><digits>` of the
 *  code itself is used — which keeps the sequence, so a pair labels "LCS1 ↔
 *  LCSD1" rather than a bare "LCS ↔ LCSD". */
function parseSuffix(sys: string | null, parentSys: string | null | undefined): Suffix | null {
  if (!sys) return null;
  let s = sys;
  if (parentSys && sys.length > parentSys.length && sys.startsWith(parentSys)) {
    s = sys.slice(parentSys.length);
  }
  const m = /([A-Za-z]+)(\d+)$/.exec(s);
  if (!m) return null;
  return { token: m[1]!.toUpperCase(), seq: m[2]! };
}

/** MS ↔ MSD, LCS ↔ LCSD — nothing else pairs. */
const PARTNER_TOKEN: Record<string, string> = { MS: 'MSD', MSD: 'MS', LCS: 'LCSD', LCSD: 'LCS' };
/** The D-member (dup) half of a pair — the row RPD is stored against. */
const D_TOKENS = new Set(['MSD', 'LCSD']);

/** The analyte key a partner must share — the parameter Oid when present (the
 *  base row's identity, not its code), else the parameter name. */
function analyteKey(row: TrendRow): string {
  return (row.parameterOid ?? row.parameter ?? '').trim().toLowerCase();
}

/** The parent key a partner must share — the parent SysSampleCode, or '' for
 *  batch QC (which has no parent but still pairs within the batch). */
function parentKey(row: TrendRow): string {
  return (row.parentSys ?? '').trim().toLowerCase();
}

interface Pair {
  partner: TrendRow;
  mySuffix: Suffix;
  partnerSuffix: Suffix;
}

/** Finds this row's duplicate partner, if any: same parent, same analyte,
 *  partner token, same sequence — all four, or …-001MS1 would pair with
 *  …-002MSD1 or with a different analyte's MSD. */
function findPartner(row: TrendRow, rows: readonly TrendRow[]): Pair | null {
  const mine = parseSuffix(row.sys, row.parentSys);
  if (!mine) return null;
  const wantToken = PARTNER_TOKEN[mine.token];
  if (!wantToken) return null;

  const myParent = parentKey(row);
  const myAnalyte = analyteKey(row);

  for (const other of rows) {
    if (other.id === row.id) continue;
    if (parentKey(other) !== myParent) continue;
    if (analyteKey(other) !== myAnalyte) continue;
    const os = parseSuffix(other.sys, other.parentSys);
    if (!os) continue;
    if (os.token === wantToken && os.seq === mine.seq) {
      return { partner: other, mySuffix: mine, partnerSuffix: os };
    }
  }
  return null;
}

// ── Route resolution ─────────────────────────────────────────────────────────

function unroutable(reason: string, c: Classified, row: TrendRow): TrendRoute {
  return {
    routable: false,
    reason,
    chartLabel: '—',
    panels: [],
    classification: c.root || '—',
    classSource: c.source,
    qcTypeName: row.qcTypeName,
    qcRole: row.qcRole,
    qcSource: row.qcSource,
    testMethodOid: row.testMethodOid,
    parameter: row.parameter,
    parameterOid: row.parameterOid,
  };
}

/** Every QC panel needs a test, a QC type and an analyte to query the series. */
function qcScopeReason(row: TrendRow, qcTypes: string[]): string | null {
  if (!row.testMethodOid) return 'No test method on this row — cannot scope its QC series.';
  if (qcTypes.every((t) => !t)) return 'No QC type on this row — cannot scope its QC series.';
  if (!row.parameter) return 'No analyte on this row — cannot scope its QC series.';
  return null;
}

function resolveOne(
  row: TrendRow,
  rows: readonly TrendRow[],
  internalStandards: Set<string>,
  micro: boolean,
): TrendRoute {
  const c = classify(row, internalStandards);
  const base = {
    classification: c.root || '—',
    classSource: c.source,
    qcTypeName: row.qcTypeName,
    qcRole: row.qcRole,
    qcSource: row.qcSource,
    testMethodOid: row.testMethodOid,
    parameter: row.parameter,
    parameterOid: row.parameterOid,
    siteName: row.siteName,
    anchorDate: row.analyzedDate ?? null,
  };

  // ── Micro (SM 9020 B) — QC ROWS ONLY ───────────────────────────────────────
  // When the batch is a microbiology test, the SAME control-chart series engine
  // charts every micro QC row under SM 9020 B rules (Wilson's ControlChartStatistics —
  // reused, not re-derived): a Duplicate → the duplicate log-range R vs the 3.27R̄
  // criterion (`rng`), a Blank → the negative-control sterility attribute
  // (`growth`), and a positive control (Spike / Standard root) → individuals on the
  // log₁₀ count (`logc`). The popup sends `scope.mode = 'micro'`, so the series
  // endpoint parses the MPN/CFU count from `Result`.
  //
  // A micro client SAMPLE is NOT handled here. Owner direction (2026-09-08): the
  // Micro Sample Trend follows the SAME v10 §1 Sample Result Trend prototype as a
  // chemistry sample — the location's approved result history vs the MCL, with the
  // Site → Project grouping fallback. So a micro Sample row FALLS THROUGH to the
  // shared Sample branch below; only its QC rows take the SM 9020 B control chart.
  if (micro && c.root !== 'Sample') {
    // `field` here is the QUANTITATIVE (count-based) SM 9020 B hint: Blank →
    // sterility, Duplicate → log-range, positive control → individuals-on-log.
    // For a QUALITATIVE Presence/Absence test (Colilert-18 / SM 9223 B — no count,
    // results are the words Present / Absent) the shared series engine detects the
    // qualitative case from the data and charts pass/fail instead (a Blank →
    // `growth`, a positive control → `pa`), IGNORING this hint (SeriesAsync only
    // honours rec/rpd/res as a forced field). So this label is a NEUTRAL default
    // correct for both cases; the popup re-labels the panel from the RESOLVED field
    // once the series loads, so the heading always matches the chart drawn
    // (see microPanelLabel / loadQcPanel in TrendChartPopup).
    const field: TrendField = c.root === 'Blank' ? 'growth' : c.root === 'Duplicate' ? 'rng' : 'logc';
    const label =
      field === 'growth'
        ? 'Sterility (SM 9020 B)'
        : field === 'rng'
          ? 'Duplicate range (SM 9020 B)'
          : 'Positive control (SM 9020 B)';
    const qcTypes = [row.qcTypeName ?? ''];
    const reason = qcScopeReason(row, qcTypes);
    if (reason) return unroutable(reason, c, row);
    return {
      ...base,
      routable: true,
      chartLabel: label,
      panels: [{ kind: 'qc', label, field, qcTypes, micro: true }],
    };
  }

  // Internal standard (Testparameter.InternalStandard) — routed BEFORE any QC
  // classification, in EVERY context. An IS (Yttrium on an ICP run) rides on every
  // sample but is not a reported analyte: it must never trend as a site history vs
  // an MCL, AND it is not in the QC control-chart dataset either (verified live:
  // Yttrium returns 0 chartable QC points), so a QC-context IS row would otherwise
  // render an empty "Blank vs RL" / "Recovery" chart under the wrong label. Its
  // only recovery series is the field-sample trend (the ratio over the run vs the
  // nominal 100%), which needs a sampling location — a QC derivative (CCB / MS / …)
  // has none, so it is honestly unroutable (disabled icon + reason) rather than a
  // misleading empty chart. Lab-flagged per (parameter × method) — no hardcoded list.
  if (row.isInternalStandard) {
    const isc: Classified = { root: 'InternalStandard', source: 'is' };
    // v10 §4 "Internal Standard Response Trend": Response % (Current ÷ Reference ×
    // 100, Reference = the batch-average response) against the method-driven
    // acceptance limits, points coloured by category — its OWN dedicated series
    // (an IS carries no QC type, so it is absent from the control-chart dataset).
    // Scoped by method × compound like the surrogate route — NO sampling location
    // required, so a QC-context IS (CCB/LCS Yttrium) charts too (the earlier
    // site-trend based 'IS recovery' could only chart client-sample IS rows).
    if (!row.testMethodOid) {
      return unroutable('No test method on this internal-standard row — cannot scope its response series.', isc, row);
    }
    if (!row.parameterOid && !row.parameter) {
      return unroutable('No compound on this internal-standard row — nothing to trend.', isc, row);
    }
    return {
      ...base,
      routable: true,
      classification: 'InternalStandard',
      classSource: 'is',
      chartLabel: 'Internal Standard Response Trend',
      panels: [{ kind: 'isresp', label: 'Internal Standard Response Trend' }],
    };
  }

  // Surrogate (SpreadSheetEntry_SampleInfo.uqTestSurrogateID = 1) — a spiked
  // recovery check that rides on a sample but is not a reported analyte, and carries
  // NO QC type, so it is absent from the QC control-chart series that every QC route
  // above reuses. Routed BEFORE the QC classification below: a surrogate row's
  // qcTypeName is the PARENT sample's category (Sample / LCS / Method Blank …), which
  // would otherwise mis-route it to that parent's site trend or recovery chart. Its
  // own chart is the surrogate recovery series for this compound × method (the
  // dedicated surrogate-trend endpoint), against its Low/High acceptance limits with
  // each point coloured by the parent sample's category (Bin Yu v10). Needs the
  // method + the compound identity to scope the series.
  if (row.isSurrogate) {
    const sc: Classified = { root: 'Surrogate', source: 'surrogate' };
    if (!row.testMethodOid) {
      return unroutable('No test method on this surrogate row — cannot scope its recovery series.', sc, row);
    }
    if (!row.parameterOid && !row.parameter) {
      return unroutable('No compound on this surrogate row — nothing to trend.', sc, row);
    }
    return {
      ...base,
      routable: true,
      classification: 'Surrogate',
      classSource: 'surrogate',
      chartLabel: 'Surrogate Recovery Trend',
      panels: [{ kind: 'surrogate', label: 'Surrogate Recovery Trend' }],
    };
  }

  // Client sample → the location's approved history vs its regulatory limit (MCL).
  // Not a pass/fail on the batch — a different question, so a different endpoint.
  // v10 §1 grouping fallback: Site → Project → "Historical Trend Not Available".
  //   • Site    (§1)  — the sample's SampleSites.SiteName; the whole-lab default.
  //   • Project (§1b) — no site, but a Samplecheckin.ProjectID: trend the PROJECT's
  //                     approved history (verified live: 1848 client-sample rows are
  //                     no-site-but-project). The panel carries `project`; the popup
  //                     calls the project-scoped endpoint and labels the header
  //                     "Project" with a "Project grouping" badge.
  //   • neither (§1c) — no site AND no project: honestly "Historical Trend Not
  //                     Available" (disabled icon + reason) rather than mixing
  //                     unrelated clients just to draw a chart.
  // (The "Configured (Client)" tier in the mock is off by design — grouping an
  //  entire client's history is too coarse to be a compliance trend.)
  if (c.root === 'Sample') {
    if (!row.parameter) return unroutable('No analyte on this row — nothing to trend.', c, row);
    if (row.siteName) {
      // Title names the data model in play (owner feedback 2026-10-03): a sample
      // WITH a sampling site trends against that site's history → "Trending with
      // Site Name".
      return {
        ...base,
        routable: true,
        chartLabel: 'Trending with Site Name',
        panels: [{ kind: 'client', label: 'Trending with Site Name', site: row.siteName }],
      };
    }
    if (row.projectName) {
      // No site → the non-site (Project) model → "Trending without Site Name".
      return {
        ...base,
        routable: true,
        chartLabel: 'Trending without Site Name',
        panels: [{ kind: 'client', label: 'Trending without Site Name', project: row.projectName }],
      };
    }
    // §1c "No scope available": a client sample with NO site AND NO project still opens
    // the popup — showing an honest "Historical Trend Not Available" empty state (v10 §1c)
    // rather than a disabled icon. Routable, with a client panel carrying NEITHER site
    // nor project; the popup detects the no-scope panel and renders the empty state.
    // No site AND no project — still the non-site model title (the empty state
    // explains the missing scope); keeps the two titles the owner asked for.
    return {
      ...base,
      routable: true,
      chartLabel: 'Trending without Site Name',
      panels: [{ kind: 'client', label: 'Trending without Site Name' }],
    };
  }

  // Internal standard (resolved via the internal-standards set rather than the
  // explicit flag) → the SAME v10 Response % chart as the flag branch above (never
  // a site trend). Kept in lock-step so both IS paths route identically.
  if (c.root === 'InternalStandard') {
    if (!row.testMethodOid) {
      return unroutable('No test method on this internal-standard row — cannot scope its response series.', c, row);
    }
    if (!row.parameterOid && !row.parameter) {
      return unroutable('No compound on this internal-standard row — nothing to trend.', c, row);
    }
    return {
      ...base,
      routable: true,
      chartLabel: 'Internal Standard Response Trend',
      panels: [{ kind: 'isresp', label: 'Internal Standard Response Trend' }],
    };
  }

  // Blank → the v10 §2 Blank Result Trend: the measured blank result judged against
  // the method-blank acceptance limit (the Reporting Limit, primary), with the
  // statistical Mean / ±2SD / ±3SD shown only as an optional informational band. A
  // creeping blank signals carryover before it fails. It has no recovery and no RPD.
  if (c.root === 'Blank') {
    const qcTypes = [row.qcTypeName ?? ''];
    const reason = qcScopeReason(row, qcTypes);
    if (reason) return unroutable(reason, c, row);
    return {
      ...base,
      routable: true,
      chartLabel: 'Blank Result Trend',
      panels: [{ kind: 'qc', label: 'Blank Result Trend', field: 'res', qcTypes }],
    };
  }

  // Duplicate → RPD only (the raw value is noise; RPD is the precision the lab
  // controls).
  if (c.root === 'Duplicate') {
    const qcTypes = [row.qcTypeName ?? ''];
    const reason = qcScopeReason(row, qcTypes);
    if (reason) return unroutable(reason, c, row);
    return {
      ...base,
      routable: true,
      chartLabel: 'Duplicate RPD Trend',
      panels: [{ kind: 'qc', label: 'Duplicate RPD Trend', field: 'rpd', qcTypes }],
    };
  }

  // Standard → recovery (Levey-Jennings + acceptance).
  if (c.root === 'Standard') {
    const qcTypes = [row.qcTypeName ?? ''];
    const reason = qcScopeReason(row, qcTypes);
    if (reason) return unroutable(reason, c, row);
    return {
      ...base,
      routable: true,
      chartLabel: 'QC Recovery Trend',
      panels: [{ kind: 'qc', label: 'QC Recovery Trend', field: 'rec', qcTypes }],
    };
  }

  // Spike → recovery, plus RPD when it is half a duplicate pair (a D-member
  // carries accuracy AND precision). QCSource splits clean-matrix (hard limits)
  // from into-a-client-sample (advisory).
  if (c.root === 'Spike') {
    const pair = findPartner(row, rows);
    if (pair) {
      const dToken = D_TOKENS.has(pair.mySuffix.token) ? pair.mySuffix : pair.partnerSuffix;
      const dRow = D_TOKENS.has(pair.mySuffix.token) ? row : pair.partner;
      const nonD = D_TOKENS.has(pair.mySuffix.token) ? pair.partnerSuffix : pair.mySuffix;

      const recTypes = Array.from(
        new Set([row.qcTypeName ?? '', pair.partner.qcTypeName ?? ''].filter((t) => t)),
      );
      const rpdTypes = [dRow.qcTypeName ?? ''];

      const reason = qcScopeReason(row, recTypes);
      if (reason) return unroutable(reason, c, row);

      // v10 §2d: a spike duplicate answers TWO questions — Precision (RPD) and
      // Accuracy (%Recovery) — shown as two panels, PRECISION FIRST (the prototype
      // order). Titles name the pair kind ("MS/MSD") and the D-member ("MSD").
      const pairKind = `${nonD.token}/${dToken.token}`;   // MS/MSD · LCS/LCSD
      return {
        ...base,
        routable: true,
        chartLabel: 'RPD Trend + Recovery Trend',
        pairLabel: `${nonD.token}${nonD.seq} ↔ ${dToken.token}${dToken.seq}`,
        // RPD against the D-member only (that is the row the duplicate difference is
        // stored against — reading the partner's recovery series there would flag every
        // point red); Recovery across both halves.
        panels: [
          { kind: 'qc', label: `Precision — ${pairKind} RPD Trend`, field: 'rpd', qcTypes: rpdTypes },
          { kind: 'qc', label: `Accuracy — ${dToken.token} Recovery Trend`, field: 'rec', qcTypes: recTypes, pointLabel: `${dToken.token} Recovery` },
        ],
      };
    }

    const qcTypes = [row.qcTypeName ?? ''];
    const reason = qcScopeReason(row, qcTypes);
    if (reason) return unroutable(reason, c, row);
    // Spiked into a client sample = advisory (the matrix, not the method, drives the
    // recovery), but the v10 prototype titles it plainly "QC Recovery Trend" either way
    // (owner 2026-09-09) — the matrix nuance lives in the description, not the label.
    return {
      ...base,
      routable: true,
      chartLabel: 'QC Recovery Trend',
      panels: [{ kind: 'qc', label: 'QC Recovery Trend', field: 'rec', qcTypes }],
    };
  }

  return unroutable('No QC classification resolved for this row.', c, row);
}

/**
 * Resolve every row's route ONCE per data change, into a map keyed by row id.
 * Pairing needs to see the whole batch, so doing it inside a cell render is
 * O(n²) — invisible on a 23-row metals batch, painful on a 600-row VOC one
 * (guide "Performance"). Call this in a useMemo over the rows and read the map
 * in the cell.
 */
export function resolveRoutes(
  rows: readonly TrendRow[],
  opts?: { internalStandards?: Iterable<string>; micro?: boolean },
): Map<string, TrendRoute> {
  const is = new Set<string>();
  for (const s of opts?.internalStandards ?? []) is.add(s.trim().toLowerCase());
  const micro = opts?.micro ?? false;

  const map = new Map<string, TrendRoute>();
  for (const row of rows) map.set(row.id, resolveOne(row, rows, is, micro));
  return map;
}
